'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode, RefObject } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';

// ---------------------------------------------------------------------------
// Shared mobile/tablet trade surface — the fixed bottom "Trade" CTA and the
// SilkSheet chrome that wraps the trade form. Both the market-detail page
// (bonding-curve form) and the `/winners` token page (AMM form) render these
// two pieces, so the button styling and the sheet behaviour are guaranteed
// identical across the two surfaces. The ONLY thing each page supplies
// differently is the inner `<TradeFormBare>` (BC vs AMM) and whether the CTA
// is disabled — every shell detail (position, size, shadow, sheet variant,
// motion, copy) lives here once.
// ---------------------------------------------------------------------------

interface MobileTradeCtaProps {
  /** Opens the trade sheet. */
  onOpen: () => void;
  /** Disable the CTA (non-tradeable market). Defaults to enabled. */
  disabled?: boolean;
  purchaseAction?: ReactNode;
  /** Optional ref forwarded onto the fixed wrapper. */
  anchorRef?: RefObject<HTMLDivElement | null>;
  /** Prefix for the `${prefix}-anchor` / `${prefix}-buy` test ids. */
  testIdPrefix: string;
}

/** Fixed bottom-of-viewport "Trade" CTA (mobile / tablet only, `lg:hidden`). */
export function MobileTradeCta(t0) {
  const $ = _c(20);
  const {
    onOpen,
    disabled: t1,
    purchaseAction,
    anchorRef,
    testIdPrefix
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const t = useTranslations("market");
  let t2;
  if ($[0] !== anchorRef) {
    t2 = anchorRef !== undefined ? {
      ref: anchorRef
    } : {};
    $[0] = anchorRef;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${testIdPrefix}-anchor`;
  let t4;
  if ($[2] !== disabled || $[3] !== onOpen) {
    t4 = () => {
      if (!disabled) {
        onOpen();
      }
    };
    $[2] = disabled;
    $[3] = onOpen;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t) {
    t5 = t("trade.buy");
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `${testIdPrefix}-buy`;
  let t7;
  if ($[7] !== t) {
    t7 = t("trade.buy");
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== disabled || $[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <AnimatedButton type="button" size="xl" className="w-full max-w-sm" disabled={disabled} onClick={t4} aria-label={t5} data-testid={t6}>{t7}</AnimatedButton>;
    $[9] = disabled;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== purchaseAction || $[16] !== t2 || $[17] !== t3 || $[18] !== t8) {
    t9 = <div {...t2} className="fixed bottom-[8px] left-0 right-0 z-overlay flex flex-col items-center gap-2 px-8 lg:hidden" data-testid={t3}>{purchaseAction}{t8}</div>;
    $[15] = purchaseAction;
    $[16] = t2;
    $[17] = t3;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
interface MobileTradeSheetShellProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** The trade form to render inside the sheet (bonding-curve or AMM). */
  children: ReactNode;
}

/** SilkSheet chrome (detached bottom sheet + auto-height) wrapping the form. */
export function MobileTradeSheetShell(t0) {
  const $ = _c(9);
  const {
    open,
    onOpenChange,
    children
  } = t0;
  const t = useTranslations("market");
  let t1;
  if ($[0] !== t) {
    t1 = t("trade.tradeNow");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <SilkSheet.Body><MotionAnimatedAutoHeight>{children}</MotionAnimatedAutoHeight></SilkSheet.Body>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onOpenChange || $[5] !== open || $[6] !== t1 || $[7] !== t2) {
    t3 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="default" surface="primary" title={t1} srOnlyHeader={true} closeButton={false} showHandle={true} keyboard={true}>{t2}</SilkSheet>;
    $[4] = onOpenChange;
    $[5] = open;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}